import { CircularProgress } from '@mui/material';
import { FC, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { PageNotFound } from '../../../../shared/lib/common/PageNotFound';
import { LINKS } from '../constants';
import { useAppContext } from '../contexts/AppContext';
import AccountSettings from '../pages/accountSettings';
import ContactUS from '../pages/contactSupport/ContactUs';
import ForgotPassword from '../pages/auth/forgotPassword';
import SignIn from '../pages/auth/login';
import ResetPassword from '../pages/auth/resetPassword';
import SignUp from '../pages/auth/signUp';
import VerifyEmail from '../pages/auth/verifyEmail';
import Billing from '../pages/billing';
import OnBoarding from '../pages/onBoarding';
import RingMyPhonePage from '../pages/ringMyPhone';
import { TermsAndConditions } from '../pages/termsAndConditions/TermsAndConditions';
import { getToken } from '../utils';
import { PrivateRoute } from './PrivateRoute';
import { PublicRoute } from './PublicRoute';
import { ChangePlan } from '../pages/changePlan';
import OptOutContact from '../pages/optOutContact';
import PrivacyPolicy from '../pages/privacyPolicy/PrivacyPolicy';
import OptInContact from '../pages/optInContact/page';
import SalesCouponPage from '../pages/salesCouponPage';
import Dashboard from '../pages/Dashboard';
import MobileHandoff from '../pages/mobileHandoff';
import MobileAccount from '../pages/mobileAccount';
import MobilePaymentHistory from '../pages/mobilePaymentHistory';

/**
 * MainRoutes is the main component that sets up the main routes for the application.
 *
 * @component
 */
const MainRoutes: FC = (): JSX.Element => {
  const { isAppUserLoggedIn, currentUser, appUserLoader } = useAppContext();
  const { isSubscribed } = currentUser ?? {};
  const { customerStripeId, subscriptionCancelled } = isSubscribed ?? {};
  const fromMobile = sessionStorage.getItem('fromMobile') === 'true';
  const shouldRedirectToOnboarding = !appUserLoader && currentUser && !customerStripeId && !subscriptionCancelled;

  const commonRouteProps = {
    element: (
      <PrivateRoute>
        <Navigate replace to={LINKS.ONBOARDING_LINK} />
      </PrivateRoute>
    )
  };

  return (
    <Routes>
      {!getToken() && !isAppUserLoggedIn && <Route path="/" element={<Navigate replace to={LINKS.LOGIN_LINK} />} />}

      {shouldRedirectToOnboarding && (
        <>
          {!fromMobile && <Route path={LINKS.CHANGE_PLAN_LINK} {...commonRouteProps} />}
          <Route path={LINKS.DASHBOARD_LINK} {...commonRouteProps} />
          <Route path={LINKS.ACCOUNT_SETTINGS} {...commonRouteProps} />
          <Route path={LINKS.BILLING_LINK} {...commonRouteProps} />
          <Route path={LINKS.RING_MY_PHONE_LINK} {...commonRouteProps} />
          <Route path={LINKS.MOBILE_ACCOUNT} {...commonRouteProps} />
          <Route path={LINKS.MOBILE_PAYMENT_HISTORY} {...commonRouteProps} />
        </>
      )}

      <Route
        path="/"
        element={
          <PublicRoute>
            <SignIn />
          </PublicRoute>
        }
      />

      <Route
        path="*"
        element={
          <PrivateRoute>
            <Suspense fallback={<CircularProgress />}>
              <PageNotFound />
            </Suspense>
          </PrivateRoute>
        }
      />

      <Route
        path={LINKS.REGISTER_LINK}
        element={
          <PublicRoute>
            <SignUp />
          </PublicRoute>
        }
      />

      <Route
        path={LINKS.LOGIN_LINK}
        element={
          <PublicRoute>
            <SignIn />
          </PublicRoute>
        }
      />

      <Route
        path={LINKS.FORGOT_PASSWORD_LINK}
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />

      <Route path={LINKS.RESET_PASSWORD} element={<ResetPassword />} />

      <Route
        path={LINKS.VERIFY_LINK}
        element={
          <PublicRoute>
            <VerifyEmail />
          </PublicRoute>
        }
      />
      <Route
        path={LINKS.OPT_OUT_LINK}
        element={
          <PublicRoute>
            <OptOutContact />
          </PublicRoute>
        }
      />

      <Route
        path={LINKS.ONBOARDING_LINK}
        element={
          <PrivateRoute>
            <OnBoarding />
          </PrivateRoute>
        }
      />
      <Route
        path={LINKS.OPT_IN_LINK}
        element={
          <PublicRoute>
            <OptInContact />
          </PublicRoute>
        }
      />

      <Route
        path={LINKS.SALES_COUPON_PAGE}
        element={
          <PublicRoute allowAuthenticated>
            <SalesCouponPage />
          </PublicRoute>
        }
      />

      <Route
        path={LINKS.DASHBOARD_LINK}
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />

      <Route
        path={LINKS.BILLING_LINK}
        element={
          <PrivateRoute>
            <Billing />
          </PrivateRoute>
        }
      />

      <Route
        path={LINKS.RING_MY_PHONE_LINK}
        element={
          <PrivateRoute>
            <RingMyPhonePage />
          </PrivateRoute>
        }
      />

      <Route path={LINKS.TERMS_AND_CONDITIONS_LINK} element={<TermsAndConditions />} />

      <Route path={LINKS.PRIVACY_POLICY_LINK} element={<PrivacyPolicy />} />

      <Route
        path={LINKS.ACCOUNT_SETTINGS}
        element={
          <PrivateRoute>
            <AccountSettings />
          </PrivateRoute>
        }
      />

      <Route
        path={LINKS.CONTACT_US_LINK}
        element={
          <PrivateRoute>
            <ContactUS />
          </PrivateRoute>
        }
      />

      <Route
        path={LINKS.CHANGE_PLAN_LINK}
        element={
          <PrivateRoute>
            <ChangePlan />
          </PrivateRoute>
        }
      />

      <Route
        path={LINKS.MOBILE_HANDOFF}
        element={
          <PublicRoute allowAuthenticated>
            <MobileHandoff />
          </PublicRoute>
        }
      />

      <Route
        path={LINKS.MOBILE_ACCOUNT}
        element={
          <PrivateRoute>
            <MobileAccount />
          </PrivateRoute>
        }
      />

      <Route
        path={LINKS.MOBILE_PAYMENT_HISTORY}
        element={
          <PrivateRoute>
            <MobilePaymentHistory />
          </PrivateRoute>
        }
      />

    </Routes>
  );
};

export default MainRoutes;
